import { createContext, useContext, useState, useEffect } from 'react';
import { getTheme, defaultTheme } from '../themes/themeConfig';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function getCustomThemeFromStorage() {
  try {
    const data = localStorage.getItem('customTheme');
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

const hexToRgb = (hex) => {
  if (!hex) return "0, 0, 0";
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    return shortResult
      ? `${parseInt(shortResult[1] + shortResult[1], 16)}, ${parseInt(shortResult[2] + shortResult[2], 16)}, ${parseInt(shortResult[3] + shortResult[3], 16)}`
      : "0, 0, 0";
  }
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
};

const ensureBrightColorInDark = (hex) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex || "#FF7A1A";
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map(x => x + x).join('');
  if (c.length !== 6) return hex;
  
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  if (brightness < 130) {
    const factor = 130 / Math.max(brightness, 1);
    const newR = Math.min(255, Math.round(r * factor + 70));
    const newG = Math.min(255, Math.round(g * factor + 70));
    const newB = Math.min(255, Math.round(b * factor + 70));
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  }
  return hex;
};

const getModeColors = (baseColors, mode) => {
  const primaryColor = baseColors.primary || "#FF7A1A";
  const secondaryColor = baseColors.secondary || baseColors.primary || "#FF9340";
  const isDefaultDark = !baseColors.name || baseColors.name.includes("NT Dark");

  if (mode === 'light') {
    const isDarkText = baseColors.text && !baseColors.text.toLowerCase().startsWith("#f") && !baseColors.text.toLowerCase().startsWith("#e");
    const textColor = isDarkText ? baseColors.text : "#0F172A";
    const textLightColor = isDarkText ? (baseColors.textLight || "#475569") : "#475569";
    const cardBg = baseColors.formBg && baseColors.formBg !== "#141416" ? baseColors.formBg : "#FFFFFF";
    const pageBg = baseColors.background && baseColors.background !== "#0B0B0C" ? baseColors.background : "#F8FAFC";

    return {
      ...baseColors,
      primary: primaryColor,
      secondary: secondaryColor,
      background: pageBg,
      formBg: cardBg,
      headerBg: "#FFFFFF",
      sidebar: baseColors.sidebar || primaryColor,
      sidebarHover: baseColors.sidebarHover || primaryColor,
      sidebarActive: baseColors.sidebarActive || primaryColor,
      boxLight: "#F1F5F9",
      text: textColor,
      textLight: textLightColor,
      border: baseColors.border && !baseColors.border.includes("rgba") ? baseColors.border : "#E2E8F0",
      inputBorder: "#CBD5E1",
      inputBg: "#F1F5F9",
      shadow: "rgba(0, 0, 0, 0.08)",
      titleAccent: primaryColor,
      sidebarActiveColor: "#FFFFFF",
    };
  }

  // DARK MODE
  const brightPrimary = ensureBrightColorInDark(primaryColor);
  const brightSecondary = ensureBrightColorInDark(secondaryColor);
  const darkPageBg = isDefaultDark ? "#0B0B0C" : "#0F172A";
  const darkCardBg = isDefaultDark ? "#141416" : "#1E293B";
  const darkHeaderBg = isDefaultDark ? "#101011" : "#1E293B";
  const darkBoxLight = isDefaultDark ? "#19191c" : "#334155";

  return {
    ...baseColors,
    primary: brightPrimary,
    secondary: brightSecondary,
    titleAccent: brightPrimary,
    sidebarActiveColor: brightPrimary,
    sidebarActive: brightPrimary,
    sidebarHover: `${brightPrimary}25`,
    sidebar: baseColors.sidebar || (isDefaultDark ? "#101011" : primaryColor),
    background: darkPageBg,
    formBg: darkCardBg,
    headerBg: darkHeaderBg,
    boxLight: darkBoxLight,
    text: "#F8FAFC",
    textLight: "#94A3B8",
    border: "rgba(255, 255, 255, 0.1)",
    inputBorder: "rgba(255, 255, 255, 0.15)",
    inputBg: "rgba(0, 0, 0, 0.25)",
    shadow: "rgba(0, 0, 0, 0.35)",
  };
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    return savedTheme || defaultTheme;
  });

  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'dark';
  });

  const [currentFont, setCurrentFont] = useState(() => {
    return localStorage.getItem('selectedFont') || 'Inter';
  });

  const [currentFontSize, setCurrentFontSize] = useState(() => {
    return localStorage.getItem('selectedFontSize') || '14px';
  });

  const [customTheme, setCustomTheme] = useState(() => getCustomThemeFromStorage());
  const [currentPath, setCurrentPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
      }
    }, 150);
    return () => clearInterval(interval);
  }, [currentPath]);

  const [themeColors, setThemeColors] = useState(() => {
    let baseColors;
    if (localStorage.getItem('selectedTheme') === 'custom') {
      const custom = getCustomThemeFromStorage();
      baseColors = custom?.colors || getTheme(defaultTheme).colors;
    } else {
      baseColors = getTheme(localStorage.getItem('selectedTheme') || defaultTheme).colors;
    }
    const mode = localStorage.getItem('themeMode') || 'dark';
    return getModeColors(baseColors, mode);
  });

  const applyThemeToDOM = (colors, font, fontSize, mode) => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.setAttribute('data-bs-theme', mode);

    const baseSize = parseInt(fontSize) || 14;
    root.style.fontSize = `${baseSize}px`;
    root.style.setProperty('--app-font-family', font);
    root.style.setProperty('--app-font-size', `${baseSize}px`);
    root.style.setProperty('--base-font-size', `${baseSize}px`);
    root.style.setProperty('--font-size', `${baseSize}px`);

    root.style.setProperty('--font-size-xs', `${Math.round(baseSize * 0.72)}px`);
    root.style.setProperty('--font-size-sm', `${Math.round(baseSize * 0.86)}px`);
    root.style.setProperty('--font-size-md', `${baseSize}px`);
    root.style.setProperty('--font-size-lg', `${Math.round(baseSize * 1.14)}px`);
    root.style.setProperty('--font-size-xl', `${Math.round(baseSize * 1.28)}px`);
    root.style.setProperty('--font-size-2xl', `${Math.round(baseSize * 1.43)}px`);
    root.style.setProperty('--font-size-3xl', `${Math.round(baseSize * 1.71)}px`);
    root.style.setProperty('--font-size-4xl', `${Math.round(baseSize * 2.0)}px`);

    if (document.body) {
      document.body.style.fontSize = `${baseSize}px`;
      document.body.style.fontFamily = font;
    }

    root.style.setProperty('--app-primary-color', colors.primary);
    root.style.setProperty('--app-primary-color-rgb', hexToRgb(colors.primary));
    root.style.setProperty('--app-primary-hover', colors.sidebarHover || colors.primary);
    root.style.setProperty('--sidebar', colors.sidebar || colors.primary);
    root.style.setProperty('--sidebarHover', colors.sidebarHover || colors.primary + 'dd');
    root.style.setProperty('--sidebarActive', colors.sidebarActive || colors.primary);
    root.style.setProperty('--app-background-color', colors.background);
    root.style.setProperty('--app-background-rgb', hexToRgb(colors.background));
    root.style.setProperty('--app-text-color', colors.text);
    root.style.setProperty('--app-form-bg-color', colors.formBg);
    root.style.setProperty('--app-border-color', colors.border);
    root.style.setProperty('--title-accent-color', colors.titleAccent || colors.primary);

    // Map NT Monitor standard CSS variables
    root.style.setProperty('--bg-main', colors.background);
    root.style.setProperty('--bg-sidebar', colors.sidebar);
    root.style.setProperty('--bg-header', colors.headerBg || (mode === 'light' ? '#FFFFFF' : '#101011'));
    root.style.setProperty('--bg-card', colors.formBg || (mode === 'light' ? '#FFFFFF' : '#141416'));
    root.style.setProperty('--bg-card-hover', colors.boxLight);
    root.style.setProperty('--border-light', colors.border);
    root.style.setProperty('--text-primary', colors.text);
    root.style.setProperty('--text-secondary', colors.textLight);
    root.style.setProperty('--text-muted', colors.textLight);
    root.style.setProperty('--accent-primary', colors.primary);
    root.style.setProperty('--accent-primary-hover', colors.sidebarHover || colors.secondary);
    root.style.setProperty('--accent-gradient', `linear-gradient(135deg, ${colors.primary}, ${colors.secondary || colors.primary})`);
    root.style.setProperty('--input-bg', colors.inputBg);
    root.style.setProperty('--input-border', colors.inputBorder || colors.border);
    root.style.setProperty('--card-shadow', colors.shadow);

    // Dynamic property mapping
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    root.style.setProperty('--font-family', font);
  };

  // Initial application
  if (typeof window !== 'undefined') {
    const initialBaseColors = currentTheme === 'custom'
      ? (customTheme?.colors || getTheme(defaultTheme).colors)
      : getTheme(currentTheme).colors;
    const activeMode = themeMode;
    const activeColors = initialBaseColors;
    applyThemeToDOM(getModeColors(activeColors, activeMode), currentFont, currentFontSize, activeMode);
  }

  // Reactive updates
  useEffect(() => {
    let baseColors;
    if (currentTheme === 'custom') {
      baseColors = customTheme?.colors || getTheme(defaultTheme).colors;
    } else {
      baseColors = getTheme(currentTheme).colors;
    }

    const activeMode = themeMode;
    const activeColors = baseColors;

    const newThemeColors = getModeColors(activeColors, activeMode);
    setThemeColors(newThemeColors);
    applyThemeToDOM(newThemeColors, currentFont, currentFontSize, activeMode);

    localStorage.setItem('selectedTheme', currentTheme);
    localStorage.setItem('selectedFont', currentFont);
    localStorage.setItem('selectedFontSize', currentFontSize);
    localStorage.setItem('themeMode', themeMode);
  }, [currentTheme, customTheme, currentFont, currentFontSize, themeMode, currentPath]);

  const changeTheme = (themeName) => setCurrentTheme(themeName);
  const changeFont = (fontName) => setCurrentFont(fontName);
  const changeFontSize = (fontSize) => setCurrentFontSize(fontSize);
  const toggleThemeMode = () => {
    setThemeMode(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const resetSettings = () => {
    localStorage.removeItem('selectedTheme');
    localStorage.removeItem('selectedFont');
    localStorage.removeItem('selectedFontSize');
    localStorage.removeItem('customTheme');
    localStorage.removeItem('themeMode');
    setCurrentTheme(defaultTheme);
    setCurrentFont('Inter');
    setCurrentFontSize('14px');
    setCustomTheme(null);
    setThemeMode('dark');
  };

  const value = {
    currentTheme,
    themeColors,
    themeMode,
    toggleThemeMode,
    setThemeMode,
    changeTheme,
    setCustomTheme,
    currentFont,
    changeFont,
    currentFontSize,
    changeFontSize,
    resetSettings,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
